export interface SolarSeal {
  id: number;
  name: string;
  englishName: string;
  color: "red" | "white" | "blue" | "yellow";
  keywords: string;
}

export interface Tone {
  id: number;
  name: string;
  englishName: string;
  action: string;
  power: string;
}

export interface GlyphResult {
  kin: number;
  tone: Tone;
  seal: SolarSeal;
  support: { seal: SolarSeal; tone: Tone };
  challenge: { seal: SolarSeal; tone: Tone };
  guide: { seal: SolarSeal; tone: Tone };
  hidden: { seal: SolarSeal; tone: Tone };
}

export const solarSeals: SolarSeal[] = [
  { id: 1, name: "紅龍", englishName: "Red Dragon", color: "red", keywords: "誕生 · 源頭 · 滋養 · 原初記憶" },
  { id: 2, name: "白風", englishName: "White Wind", color: "white", keywords: "溝通 · 訊息 · 靈感 · 呼吸" },
  { id: 3, name: "藍夜", englishName: "Blue Night", color: "blue", keywords: "夢境 · 潛意識 · 豐盛 · 直覺" },
  { id: 4, name: "黃種子", englishName: "Yellow Seed", color: "yellow", keywords: "覺察 · 潛能 · 耐心 · 開花" },
  { id: 5, name: "紅蛇", englishName: "Red Serpent", color: "red", keywords: "生命力 · 本能 · 熱情 · 蛻變" },
  { id: 6, name: "白世界橋", englishName: "White Worldbridger", color: "white", keywords: "連結 · 臣服 · 橋樑 · 放下" },
  { id: 7, name: "藍手", englishName: "Blue Hand", color: "blue", keywords: "完成 · 療癒 · 實踐 · 知曉" },
  { id: 8, name: "黃星星", englishName: "Yellow Star", color: "yellow", keywords: "優雅 · 藝術 · 和諧 · 美感" },
  { id: 9, name: "紅月", englishName: "Red Moon", color: "red", keywords: "流動 · 淨化 · 情感 · 宇宙之水" },
  { id: 10, name: "白狗", englishName: "White Dog", color: "white", keywords: "愛 · 忠誠 · 心 · 夥伴" },
  { id: 11, name: "藍猴", englishName: "Blue Monkey", color: "blue", keywords: "遊戲 · 幻象 · 幽默 · 創造" },
  { id: 12, name: "黃人", englishName: "Yellow Human", color: "yellow", keywords: "自由意志 · 智慧 · 選擇 · 影響" },
  { id: 13, name: "紅天行者", englishName: "Red Skywalker", color: "red", keywords: "探索 · 空間 · 覺醒 · 勇氣" },
  { id: 14, name: "白巫師", englishName: "White Wizard", color: "white", keywords: "永恆 · 接受 · 魔法 · 臨在" },
  { id: 15, name: "藍鷹", englishName: "Blue Eagle", color: "blue", keywords: "願景 · 心智 · 宏觀 · 創造" },
  { id: 16, name: "黃戰士", englishName: "Yellow Warrior", color: "yellow", keywords: "智慧 · 無懼 · 提問 · 路徑" },
  { id: 17, name: "紅地球", englishName: "Red Earth", color: "red", keywords: "導航 · 共時 · 紮根 · 演化" },
  { id: 18, name: "白鏡", englishName: "White Mirror", color: "white", keywords: "秩序 · 反映 · 真實 · 清明" },
  { id: 19, name: "藍風暴", englishName: "Blue Storm", color: "blue", keywords: "催化 · 轉化 · 更新 · 自我生成" },
  { id: 20, name: "黃太陽", englishName: "Yellow Sun", color: "yellow", keywords: "開悟 · 整合 · 溫暖 · 生命" },
];

export const tones: Tone[] = [
  { id: 1, name: "磁性的", englishName: "Magnetic", action: "目的是", power: "統一" },
  { id: 2, name: "月亮的", englishName: "Lunar", action: "挑戰是", power: "極化" },
  { id: 3, name: "電力的", englishName: "Electric", action: "服務是", power: "活化" },
  { id: 4, name: "自我存在的", englishName: "Self-Existing", action: "形式是", power: "定義" },
  { id: 5, name: "超頻的", englishName: "Overtone", action: "放射是", power: "賦予力量" },
  { id: 6, name: "韻律的", englishName: "Rhythmic", action: "組織是", power: "平等" },
  { id: 7, name: "共鳴的", englishName: "Resonant", action: "引導是", power: "調頻" },
  { id: 8, name: "銀河的", englishName: "Galactic", action: "和諧是", power: "整合" },
  { id: 9, name: "太陽的", englishName: "Solar", action: "意圖是", power: "脈衝" },
  { id: 10, name: "行星的", englishName: "Planetary", action: "完美是", power: "顯化" },
  { id: 11, name: "光譜的", englishName: "Spectral", action: "消解是", power: "釋放" },
  { id: 12, name: "水晶的", englishName: "Crystal", action: "合作是", power: "奉献" },
  { id: 13, name: "宇宙的", englishName: "Cosmic", action: "存在是", power: "超越" },
];

export function getSealById(id: number): SolarSeal {
  const normalized = ((id - 1) % 20) + 1;
  return solarSeals[normalized - 1];
}

export function getToneById(id: number): Tone {
  const normalized = ((id - 1) % 13) + 1;
  return tones[normalized - 1];
}

export function getSealColorClass(color: string): string {
  switch (color) {
    case "red":
      return "bg-red-500";
    case "white":
      return "bg-stone-200 border border-stone-300";
    case "blue":
      return "bg-sky-600";
    case "yellow":
      return "bg-amber-400";
    default:
      return "bg-stone-400";
  }
}

export function getSealTextColor(color: string): string {
  switch (color) {
    case "red":
      return "text-red-600";
    case "white":
      return "text-stone-500";
    case "blue":
      return "text-sky-600";
    case "yellow":
      return "text-amber-600";
    default:
      return "text-stone-600";
  }
}

export function getSealBorderClass(color: string): string {
  switch (color) {
    case "red":
      return "border-red-200";
    case "white":
      return "border-stone-200";
    case "blue":
      return "border-sky-200";
    case "yellow":
      return "border-amber-200";
    default:
      return "border-stone-200";
  }
}

export function getSealBgClass(color: string): string {
  switch (color) {
    case "red":
      return "bg-red-50/70";
    case "white":
      return "bg-white/80";
    case "blue":
      return "bg-sky-50/70";
    case "yellow":
      return "bg-amber-50/70";
    default:
      return "bg-white/80";
  }
}

// Dreamspell base date: 1987-07-26 = Kin 34
const BASE_YEAR = 1987;
const BASE_MONTH = 7;
const BASE_DAY = 26;
const BASE_KIN = 34;

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

function daysInMonth(year: number, month: number): number {
  const days = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return days[month - 1];
}

// Count how many Feb 29 days exist between two dates (exclusive of start, inclusive of end)
function countLeapDaysBetween(startYear: number, startMonth: number, startDay: number, endYear: number, endMonth: number, endDay: number): number {
  let count = 0;
  const startDate = new Date(startYear, startMonth - 1, startDay);
  const endDate = new Date(endYear, endMonth - 1, endDay);

  for (let y = startYear; y <= endYear; y++) {
    if (!isLeapYear(y)) continue;
    const leapDate = new Date(y, 1, 29); // Feb 29
    if (leapDate > startDate && leapDate <= endDate) {
      count++;
    }
  }
  return count;
}

// Calculate total days between two dates
function daysBetween(year1: number, month1: number, day1: number, year2: number, month2: number, day2: number): number {
  const d1 = new Date(year1, month1 - 1, day1);
  const d2 = new Date(year2, month2 - 1, day2);
  return Math.floor((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
}

export function isLeapDay(year: number, month: number, day: number): boolean {
  return month === 2 && day === 29 && isLeapYear(year);
}

export function calculateDreamspell(year: number, month: number, day: number): GlyphResult | null {
  if (isLeapDay(year, month, day)) {
    return null; // Feb 29 is Hunab Ku day, no Kin
  }

  const totalDays = daysBetween(BASE_YEAR, BASE_MONTH, BASE_DAY, year, month, day);
  const leapDays = countLeapDaysBetween(BASE_YEAR, BASE_MONTH, BASE_DAY, year, month, day);
  const dreamspellDays = totalDays - leapDays;

  const kin = ((BASE_KIN - 1 + dreamspellDays) % 260) + 1;
  const toneId = ((kin - 1) % 13) + 1;
  const sealId = ((kin - 1) % 20) + 1;

  const tone = getToneById(toneId);
  const seal = getSealById(sealId);

  // Support: 19 - seal (wrap 1..20), same tone
  const supportSealId = ((19 - sealId - 1 + 20) % 20) + 1;
  const supportSeal = getSealById(supportSealId);

  // Challenge: seal + 10 (wrap), same tone
  const challengeSealId = ((sealId + 10 - 1) % 20) + 1;
  const challengeSeal = getSealById(challengeSealId);

  // Hidden: 21 - seal, tone = 14 - tone
  const hiddenSealId = ((21 - sealId - 1 + 20) % 20) + 1;
  const hiddenToneId = ((14 - toneId - 1 + 13) % 13) + 1;
  const hiddenSeal = getSealById(hiddenSealId);
  const hiddenTone = getToneById(hiddenToneId);

  // Guide: same tone, seal + offset
  // offset: 1/6/11→0; 2/7/12→12; 3/8/13→4; 4/9→16; 5/10→8
  let offset = 0;
  if (toneId === 1 || toneId === 6 || toneId === 11) offset = 0;
  else if (toneId === 2 || toneId === 7 || toneId === 12) offset = 12;
  else if (toneId === 3 || toneId === 8 || toneId === 13) offset = 4;
  else if (toneId === 4 || toneId === 9) offset = 16;
  else if (toneId === 5 || toneId === 10) offset = 8;

  const guideSealId = ((sealId + offset - 1) % 20) + 1;
  const guideSeal = getSealById(guideSealId);

  return {
    kin,
    tone,
    seal,
    support: { seal: supportSeal, tone },
    challenge: { seal: challengeSeal, tone },
    guide: { seal: guideSeal, tone },
    hidden: { seal: hiddenSeal, tone: hiddenTone },
  };
}

export function getToneName(toneId: number): string {
  const tone = getToneById(toneId);
  return `${tone.englishName} ${tone.name}`;
}

export function getSealFullName(seal: SolarSeal, tone: Tone): string {
  return `${tone.englishName} ${seal.englishName} · ${tone.name}${seal.name}`;
}

export function getSealImageUrl(sealId: number): string {
  const images: Record<number, string> = {
    1: "https://pic2.zhimg.com/v2-ca201aa2e236963c5f3eda669c4b48f5_360w.jpg",
    2: "https://picx.zhimg.com/v2-ff1b4473035fcc202856b1e5c5b7e30b_360w.jpg",
    3: "https://picx.zhimg.com/v2-280f2ee4b3e47158b194d0ad4219e6ab_360w.jpg",
    4: "https://pic1.zhimg.com/v2-c22ed003a0bf8ef7db3dcaa867b50316_360w.jpg",
    5: "https://pica.zhimg.com/v2-cfd51b0bcb8730f258b1e075a840ddb6_360w.jpg",
    6: "https://pica.zhimg.com/v2-71a18c75755e0f0e88bb9ed45f160ac0_360w.jpg",
    7: "https://pic4.zhimg.com/v2-80a3fce24a8cea72b678299da4f72e91_360w.jpg",
    8: "https://picx.zhimg.com/v2-1d28afdf1d85bcbcd8d44d5830ed56a1_360w.jpg",
    9: "https://pic2.zhimg.com/v2-c03163914fe88b98830f44d5b6414551_360w.jpg",
    10: "https://pic4.zhimg.com/v2-596c6a21a1f59f2a1f60331458e8fecb_360w.jpg",
    11: "https://pic4.zhimg.com/v2-6ea65e0fbf2dde680f15dd290d027d3b_360w.jpg",
    12: "https://pic3.zhimg.com/v2-ba5459d4c2499752f6261850c83235be_360w.jpg",
    13: "https://pic3.zhimg.com/v2-7d8943660e4405cae3e9e3c4ec0fea5c_360w.jpg",
    14: "https://pic4.zhimg.com/v2-708a156145a9e2eadc48fb98e674424d_360w.jpg",
    15: "https://pica.zhimg.com/v2-c7e1cdd1711c40373ad509820740f154_360w.jpg",
    16: "https://pic4.zhimg.com/v2-7d2eed4e9f284355a9e35ae86b05040f_360w.jpg",
    17: "https://pica.zhimg.com/v2-737fc4e0e012e568f403e5caafd1b568_360w.jpg",
    18: "https://pic1.zhimg.com/v2-2916ef5e3f62662153ca8e76066c1914_360w.jpg",
    19: "https://pic2.zhimg.com/v2-3408bfc69fdc0fd34c7178992984e283_360w.jpg",
    20: "https://pic4.zhimg.com/v2-0efd0f4fcf6ded63e8f154089942dd65_360w.jpg",
  };
  return images[sealId] || "";
}